import {useState} from 'react'
import PropTypes from 'prop-types'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import {useNavigate} from 'react-router-dom'
import Collapse from '@mui/material/Collapse'
import Typography from '@mui/material/Typography'
import IconButton from '@mui/material/IconButton'
import {useGetGroupDetails} from '../api/getProjectDetails'
import AddProjectModel from './AddProjectModel'
import {
  GappedRow,
  StyledArrowForwardIcon,
  StyledClientName,
  StyledEditIcon,
  StyledExpandIcon,
  StyledPaper
} from './ClientAccordionStyle'
import ClientAccordionContent from './ClientAccordionContent'
import AddCircleRoundedIcon from '@mui/icons-material/AddCircleRounded'
import ClientNameEditModal from '../common/ClientNameEditModal/ClientNameEditModal'
import {useRolePermissions} from '../../hooks/useRolesPermissions'

const ClientAccordion = ({client, onClientUpdated, refetchClients}) => {
  const [isExpanded, setIsExpanded] = useState(false)
  const [showAddProjectModal, setShowAddProjectModal] = useState(false)
  const [showEditClientModal, setShowEditClientModal] = useState(false)

  const {isSuperAdmin, isCustomerSupport} = useRolePermissions()

  const navigate = useNavigate()
  const groupDetails = useGetGroupDetails('' + (client?.CLIENT_ID || ''), !isExpanded || !client?.CLIENT_ID)
  const existingProjects = groupDetails?.group?.groupList?.GROUPList?.map((group) => group.GROUP_NAME) || []

  if (!client?.CLIENT_ID) return null

  const toggleCollapse = (event) => {
    event.stopPropagation()
    setIsExpanded((prev) => !prev)
  }

  const handleClientEdit = (event) => {
    event.stopPropagation()
    setShowEditClientModal(true)
  }

  const handleViewClient = (event) => {
    event.stopPropagation()
    navigate(`/adminpanel/client/${client.CLIENT_ID}`)
  }

  const handleCreateProject = (event) => {
    event.stopPropagation()
    setShowAddProjectModal(true)
  }

  const handleProjectAdded = async () => {
    await groupDetails?.refetchGroups()
    await refetchClients()
  }

  return (
    <>
      {showEditClientModal && (
        <ClientNameEditModal
          open={showEditClientModal}
          onClose={() => setShowEditClientModal(false)}
          clientId={client.CLIENT_ID}
          clientName={client.CLIENT_NAME}
          onSave={onClientUpdated}
        />
      )}
      {showAddProjectModal && (
        <AddProjectModel
          clientId={client.CLIENT_ID}
          onClose={() => setShowAddProjectModal(false)}
          open={showAddProjectModal}
          onSave={handleProjectAdded}
          existingProjects={existingProjects}
        />
      )}
      <StyledPaper onClick={toggleCollapse} variant='outlined'>
        <Box display='flex' justifyContent='space-between' alignItems='center'>
          <GappedRow isClickable>
            <StyledExpandIcon isExpanded={isExpanded} />
            <StyledClientName variant='h6'>{client.CLIENT_NAME}</StyledClientName>
          </GappedRow>
          <Box display='flex' alignItems='center' gap={10}>
            <GappedRow>
              <Typography variant='body2' color='textSecondary'>
                Total Projects: {client.TOTAL_GROUPS}
              </Typography>
              <Typography variant='body2' color='InactiveBorder'>
                |
              </Typography>
              <Typography variant='body2' color='textSecondary'>
                Active Members: {client.TOTAL_ACTIVE_USERS}
              </Typography>
            </GappedRow>
            <Box display='flex' alignItems='center' gap={0}>
              {(isSuperAdmin() || isCustomerSupport()) && (
                <IconButton onClick={handleClientEdit} color='primary' size='small' aria-label='Edit client'>
                  <StyledEditIcon />
                </IconButton>
              )}
              <IconButton onClick={handleViewClient} color='primary' size='small' aria-label='View client details'>
                <StyledArrowForwardIcon />
              </IconButton>
            </Box>
          </Box>
        </Box>
        <Collapse in={isExpanded} timeout={300}>
          {isExpanded && groupDetails && (
            <Box ml={3} mt={2}>
              <Button onClick={handleCreateProject} endIcon={<AddCircleRoundedIcon />} variant='text'>
                Projects
              </Button>
              <ClientAccordionContent group={groupDetails} />
            </Box>
          )}
        </Collapse>
      </StyledPaper>
    </>
  )
}

ClientAccordion.propTypes = {
  client: PropTypes.shape({
    CLIENT_ID: PropTypes.string.isRequired,
    CLIENT_NAME: PropTypes.string.isRequired,
    TOTAL_GROUPS: PropTypes.number.isRequired,
    TOTAL_ACTIVE_USERS: PropTypes.number.isRequired
  }).isRequired,
  onClientUpdated: PropTypes.func,
  refetchClients: PropTypes.func.isRequired
}

ClientAccordion.defaultProps = {
  onClientUpdated: undefined
}

export default ClientAccordion
